import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link, Redirect, useHistory, useParams } from "react-router-dom";
import { TbCheck, TbKey, TbLink, TbList, TbMailForward, TbLogout, TbPlayerPlay, TbShield, TbTrash } from "react-icons/tb";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../Hooks/toast";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { BaasEntidadesPageSkeleton } from "../components/BaasSkeleton";
import {
  BaasProposalSituationCard,
} from "../components/BaasProposalSituationBadge";
import { mapBusinessEntityResponseToForm } from "../../../../Services/baasBusinessAccountApi";
import { fetchBaasAccountsForEntity } from "../../../../Services/baasAccountsListApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import {
  activateBaasEntity,
  approveBaasAccountProposal,
  approveBaasEntityVerification,
  deactivateBaasEntity,
  fetchBaasCarteiraPresence,
  isBaasEntityDeactivated,
  isEntidadeContaInactive,
  fetchBaasEntityById,
  isBaasEntityAwaitingFirstAccess,
  isValutMatrixConsolePin,
  resendBaasEntityAccessEmail,
  resolveEntidadeDetailContaStatus,
  resolveBaasAccountExternalId,
  resetBaasEntityTransactionalPassword,
  revokeBaasCarteiraSession,
} from "../../../../Services/baasEntidadesApi";
import { fetchBaasInstanceDetail, fetchBaasInstanceFeatures } from "../../../../Services/baasInstancesApi";
import {
  isBaasInstanceOwnerEntity,
  resolveBaasInstanceOwnerEntityId,
} from "../../../../Services/baasVirtualShells";
import { mapPersonEntityResponseToForm } from "../../../../Services/baasPersonalAccountApi";
import { BAAS_ONBOARD_PATH, BAAS_ROOT_PATH } from "../constants";
import {
  resolveProposalSituation,
} from "../../../../Utils/baasProposalSituation";
import { BaasLoggedInDot } from "../components/BaasRowActionMenu";
import { resolveBaasPresenceDot } from "../../../../Utils/baasPresenceDot";
import { ENTIDADES_LIST_PATH } from "./BaasEntidadeCreatePage";
import { BaasEntidadeCreateForm } from "./components/BaasEntidadeCreateForm";
import { BaasEntidadePersonalCreateForm } from "./components/BaasEntidadePersonalCreateForm";
import { BaasEntidadeProductsPanel } from "./components/BaasEntidadeProductsPanel";
import { BaasEntidadeRelatedAccountsPanel } from "./components/BaasEntidadeRelatedAccountsPanel";
import { BaasEntidadeKycGenerateModal } from "./components/BaasEntidadeKycGenerateModal";
import { ShortcutCard } from "../../../../Components/Atoms/ShortcutCard";
import { BaasConfirmModal } from "../components/BaasConfirmModal";

export const ENTIDADES_DETAIL_PATH = "/baas/entidades/:entityId";

export const buildEntidadeDetailPath = (entityId) =>
  `/baas/entidades/${encodeURIComponent(entityId)}`;

/** Prefer Baas `acct_…` external id over account number for proposal routes. */
const resolveApproveAccountId = (account) => resolveBaasAccountExternalId(account);

const statusStyles = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  suspended: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
  none: "bg-gray-100 text-gray-500 dark:bg-brand-dark-elevated dark:text-gray-400",
};

const kybStyles = {
  approved: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  in_review: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  rejected: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
};

const accessStatusStyles = {
  completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  no_email: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
};

export const BaasEntidadeDetailPage = () => {
  const { entityId: routeEntityId } = useParams();
  const entityId = routeEntityId ? decodeURIComponent(routeEntityId) : "";
  const history = useHistory();
  const { selectedInstance, opsInstance, clearPeerOpsPin } = useBaasInstance();
  // Soft peer target for APIs; sidebar pin stays on Valut (selectedInstance).
  const apiInstance = opsInstance || selectedInstance;
  const { addToast } = useToast();
  const { can } = useBaasCapabilities();
  const { isValutConfigAdmin, canUseViewAs, loading: valutAdminLoading } = useBaasPlatformCreateAccess();
  // Real Valut INFRA / matrix admin (not suppressed by client "Visualizar como" personas).
  const isValutAdmin = Boolean(isValutConfigAdmin || canUseViewAs);
  const [ownerEntityId, setOwnerEntityId] = useState(() =>
    resolveBaasInstanceOwnerEntityId(apiInstance),
  );
  const isInstanceOwner = isBaasInstanceOwnerEntity(
    { id: entityId },
    ownerEntityId,
  );
  const canEditEntity =
    (can("entities.create") || can("entities.update"))
    && (!isInstanceOwner || isValutAdmin);
  const canCreateAccount = can("accounts.create");
  const canCreateInvites = can("entities.create");
  const canResendAccess = can("entities.create") || can("entities.update");
  const pageLoading = useBaasPageLoading(
    `${selectedInstance?.id || ""}:${apiInstance?.id || ""}`,
  );
  const [entity, setEntity] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [accountsResolved, setAccountsResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [documentsUploadEnabled, setDocumentsUploadEnabled] = useState(false);
  const [approvingAccount, setApprovingAccount] = useState(false);
  const [approvingEntity, setApprovingEntity] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [resendingAccess, setResendingAccess] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null);
  const [retainBalance, setRetainBalance] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [detailTab, setDetailTab] = useState("dados");
  const [generateOpen, setGenerateOpen] = useState(false);
  const [inviteFeatureEnabled, setInviteFeatureEnabled] = useState(false);

  const DETAIL_TABS = useMemo(
    () => [
      { id: "dados", labelKey: "baas.entidades.detail.tabs.dados" },
      { id: "produtos", labelKey: "baas.entidades.detail.tabs.produtos" },
    ],
    [],
  );

  useEffect(() => {
    let cancelled = false;
    if (!apiInstance?.baasInstanceId || !canCreateInvites) {
      setInviteFeatureEnabled(false);
      return undefined;
    }
    fetchBaasInstanceFeatures(apiInstance)
      .then((features) => {
        if (cancelled) return;
        const list = Array.isArray(features) ? features : [];
        const invite = list.find(
          (item) =>
            String(item?.featureCode || "")
              .trim()
              .toLowerCase() === "kyc.invite_link",
        );
        setInviteFeatureEnabled(Boolean(invite?.enabled));
      })
      .catch(() => {
        if (!cancelled) setInviteFeatureEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiInstance, canCreateInvites]);

  useEffect(() => {
    let cancelled = false;

    if (!apiInstance) {
      setDocumentsUploadEnabled(false);
      setOwnerEntityId("");
      return undefined;
    }

    setDocumentsUploadEnabled(Boolean(apiInstance.allowSkipKyc) || isValutAdmin);
    const fromSelection = resolveBaasInstanceOwnerEntityId(apiInstance);
    if (fromSelection) {
      setOwnerEntityId(fromSelection);
    }

    (async () => {
      try {
        const detail = await fetchBaasInstanceDetail(apiInstance);
        if (!cancelled) {
          setDocumentsUploadEnabled(Boolean(detail?.allowSkipKyc) || isValutAdmin);
          if (!fromSelection) {
            setOwnerEntityId(resolveBaasInstanceOwnerEntityId(detail));
          }
        }
      } catch {
        if (!cancelled) {
          setDocumentsUploadEnabled(isValutAdmin);
          if (!fromSelection) setOwnerEntityId("");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isValutAdmin, apiInstance]);

  useEffect(() => {
    let cancelled = false;

    if (!apiInstance || !entityId) {
      setEntity(null);
      setProposal(null);
      setLinkedAccounts([]);
      setAccountsResolved(false);
      setLoading(false);
      setError("");
      return undefined;
    }

    setLoading(true);
    setError("");
    setAccountsResolved(false);

    (async () => {
      try {
        const next = await fetchBaasEntityById(apiInstance, entityId);
        if (cancelled) return;
        if (!next) {
          setEntity(null);
          setProposal(null);
          setLinkedAccounts([]);
          setAccountsResolved(true);
          setError(intl.get("baas.entidades.detail.notFound"));
          return;
        }

        // Entity first (list fields → Conta). Status from webhook → local
        // proposalSituation / Account flags only. Live PaySmart GET is only
        // Contas "Consultar proposta" (manual) — never on enter/reload.
        let hydrated = next;
        if (
          isValutMatrixConsolePin(selectedInstance)
          && next.personId
          && !next.isPeerInstanceOwner
        ) {
          try {
            const logged = await fetchBaasCarteiraPresence([next.personId]);
            hydrated = {
              ...next,
              loggedInCarteira: logged.has(Number(next.personId)),
            };
          } catch {
            hydrated = next;
          }
        }
        setEntity(hydrated);
        const nextAccounts = await fetchBaasAccountsForEntity(apiInstance, next).catch(() => []);
        if (cancelled) return;
        const accounts = Array.isArray(nextAccounts) ? nextAccounts : [];
        setLinkedAccounts(accounts);
        setAccountsResolved(true);
        if (accounts.length > 0 && hydrated.hasAccount !== true) {
          setEntity({ ...hydrated, hasAccount: true });
        }

        const localSituation =
          resolveProposalSituation(hydrated.proposalSituation || null, hydrated)
          || accounts
            .map((account) => resolveProposalSituation(account?.proposalSituation || null, account))
            .find(Boolean)
          || null;
        if (cancelled) return;
        setProposal(localSituation ? { situation: localSituation } : null);
      } catch {
        if (!cancelled) {
          setEntity(null);
          setProposal(null);
          setLinkedAccounts([]);
          setAccountsResolved(true);
          setError(intl.get("baas.entidades.detail.loadError"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiInstance, selectedInstance, entityId, reloadToken]);

  const businessForm = useMemo(() => {
    if (!entity || entity.type !== "pj" || !entity.raw) return null;
    return mapBusinessEntityResponseToForm(entity.raw);
  }, [entity]);

  const personalForm = useMemo(() => {
    if (!entity || entity.type !== "pf" || !entity.raw) return null;
    return mapPersonEntityResponseToForm(entity.raw);
  }, [entity]);

  const handleEntityUpdated = (updated) => {
    if (updated) {
      setEntity(updated);
    } else {
      setReloadToken((token) => token + 1);
    }
  };

  /** Soft-refresh entity docs only — keep Documentos sub-tab / form mounted (no skeleton). */
  const handleDocumentsUploaded = useCallback(async () => {
    if (!apiInstance || !entityId) {
      setReloadToken((token) => token + 1);
      return;
    }
    try {
      const next = await fetchBaasEntityById(apiInstance, entityId);
      if (next) setEntity(next);
    } catch {
      setReloadToken((token) => token + 1);
    }
  }, [apiInstance, entityId]);

  const handleApproveEntity = useCallback(async () => {
    if (!apiInstance || !entityId || approvingEntity) return;
    const confirmed = window.confirm(intl.get("baas.entidades.detail.approveEntityConfirm"));
    if (!confirmed) return;

    setApprovingEntity(true);
    setApproveError("");
    try {
      await approveBaasEntityVerification(apiInstance, entityId);
      addToast({
        type: "success",
        title: intl.get("baas.entidades.detail.approveEntitySuccess"),
      });
      setReloadToken((token) => token + 1);
    } catch (err) {
      const message =
        err?.response?.data?.detail
        || err?.detail
        || err?.response?.data?.error
        || err?.code
        || err?.message
        || intl.get("baas.entidades.detail.approveEntityFailed");
      setApproveError(message);
    } finally {
      setApprovingEntity(false);
    }
  }, [addToast, approvingEntity, entityId, apiInstance]);

  const handleApproveAccount = useCallback(async () => {
    const primaryAccount = linkedAccounts.find((item) => resolveApproveAccountId(item)) || null;
    const accountId = resolveApproveAccountId(primaryAccount);
    if (!apiInstance || !entityId || !accountId || approvingAccount) return;

    setApprovingAccount(true);
    setApproveError("");
    try {
      const next = await approveBaasAccountProposal(
        apiInstance,
        entityId,
        accountId,
        {
          proposalId: proposal?.proposalId || primaryAccount?.proposalId,
        },
      );
      if (next) {
        setProposal(next);
      }
      setReloadToken((token) => token + 1);
    } catch (err) {
      const message =
        err?.response?.data?.detail
        || err?.detail
        || err?.response?.data?.error
        || err?.code
        || err?.message
        || intl.get("baas.accounts.quickActions.approveAccountFailed");
      setApproveError(message);
    } finally {
      setApprovingAccount(false);
    }
  }, [
    apiInstance,
    entityId,
    linkedAccounts,
    proposal,
    approvingAccount,
  ]);

  const handleResendAccessEmail = useCallback(() => {
    if (!apiInstance || !entityId || resendingAccess) return;
    if (entity?.type !== "pf" && !String(entityId || "").startsWith("pers_")) return;

    const personEmail =
      entity?.raw?.person_details?.email ||
      entity?.raw?.personDetails?.email ||
      "";
    if (entity?.accessStatus === "no_email" || !String(personEmail).includes("@")) {
      addToast({
        type: "error",
        title: intl.get("baas.users.access.resendFailed"),
        description: intl.get("baas.users.access.noEmail"),
      });
      return;
    }

    setConfirmKind("resend");
    setConfirmError("");
  }, [addToast, entity, entityId, resendingAccess, apiInstance]);

  const handleResendAccessConfirmed = useCallback(async () => {
    if (!apiInstance || !entityId || resendingAccess) return;
    setResendingAccess(true);
    setConfirmError("");
    try {
      const result = await resendBaasEntityAccessEmail(apiInstance, entityId);
      const email = String(result?.email || "").trim();
      addToast({
        type: "success",
        title: email
          ? intl.get("baas.users.access.resendSuccessTo", { email })
          : intl.get("baas.users.access.resendSuccess"),
      });
      setConfirmKind(null);
    } catch (err) {
      setConfirmError(
        formatBaasConsoleConflictMessage(err, err?.detail || err?.message || null),
      );
    } finally {
      setResendingAccess(false);
    }
  }, [addToast, entityId, resendingAccess, apiInstance]);

  const handleCarteiraLogout = useCallback(async () => {
    if (!entity?.personId || !entity.loggedInCarteira) return;
    const confirmed = window.confirm(
      intl.get("baas.entidades.logoutConfirm", { name: entity.name || "" }),
    );
    if (!confirmed) return;
    try {
      await revokeBaasCarteiraSession(entity.personId);
      addToast({ type: "success", title: intl.get("baas.entidades.logoutSuccess") });
      setReloadToken((token) => token + 1);
    } catch (err) {
      addToast({
        type: "error",
        title: intl.get("baas.entidades.logoutFailed"),
        description: formatBaasConsoleConflictMessage(err, err?.detail || err?.message || null),
      });
    }
  }, [addToast, entity]);

  const handleResetTransactionalPassword = useCallback(async () => {
    if (!entity?.personId) return;
    if (isBaasEntityAwaitingFirstAccess(entity)) return;
    const confirmed = window.confirm(
      intl.get("baas.entidades.txPasswordResetConfirm", { name: entity.name || "" }),
    );
    if (!confirmed) return;
    try {
      await resetBaasEntityTransactionalPassword(entity.personId);
      addToast({ type: "success", title: intl.get("baas.entidades.txPasswordResetSuccess") });
    } catch (err) {
      addToast({
        type: "error",
        title: intl.get("baas.entidades.txPasswordResetFailed"),
        description: err?.code === "first_access_incomplete"
          || err?.code === "transactional_password_not_set"
          ? intl.get("baas.entidades.firstAccessRequiredHint")
          : formatBaasConsoleConflictMessage(err, err?.detail || err?.message || null),
      });
    }
  }, [addToast, entity]);

  const closeConfirmModal = useCallback(() => {
    if (deactivating || activating || resendingAccess) return;
    setConfirmKind(null);
    setConfirmError("");
    setRetainBalance(false);
  }, [activating, deactivating, resendingAccess]);

  const handleDeactivateEntity = useCallback(() => {
    if (!apiInstance || !entityId || deactivating) return;
    setRetainBalance(false);
    setConfirmError("");
    setConfirmKind("deactivate");
  }, [deactivating, entityId, apiInstance]);

  const handleDeactivateConfirmed = useCallback(async () => {
    if (!apiInstance || !entityId || deactivating) return;
    setDeactivating(true);
    setConfirmError("");
    try {
      try {
        await deactivateBaasEntity(apiInstance, entityId, { retainBalance });
      } catch (err) {
        if (err?.code === "balance_destination_required") {
          if (!retainBalance) {
            setConfirmError(intl.get("baas.entidades.deactivateRetain"));
            return;
          }
          await deactivateBaasEntity(apiInstance, entityId, { retainBalance: true });
        } else {
          throw err;
        }
      }
      addToast({ type: "success", title: intl.get("baas.entidades.deactivateSuccess") });
      setConfirmKind(null);
      setRetainBalance(false);
      setReloadToken((token) => token + 1);
    } catch (err) {
      setConfirmError(
        formatBaasConsoleConflictMessage(err, err?.detail || err?.message || null),
      );
    } finally {
      setDeactivating(false);
    }
  }, [addToast, deactivating, entityId, retainBalance, apiInstance]);

  const handleActivateEntity = useCallback(() => {
    if (!apiInstance || !entityId || activating) return;
    setConfirmError("");
    setConfirmKind("activate");
  }, [activating, entityId, apiInstance]);

  const handleActivateConfirmed = useCallback(async () => {
    if (!apiInstance || !entityId || activating) return;
    setActivating(true);
    setConfirmError("");
    try {
      await activateBaasEntity(apiInstance, entityId);
      addToast({ type: "success", title: intl.get("baas.entidades.activateSuccess") });
      setConfirmKind(null);
      setReloadToken((token) => token + 1);
    } catch (err) {
      setConfirmError(
        formatBaasConsoleConflictMessage(err, err?.detail || err?.message || null),
      );
    } finally {
      setActivating(false);
    }
  }, [addToast, activating, entityId, apiInstance]);

  const handleDocumentUpdated = (updated) => {
    if (!updated?.id) {
      setReloadToken((token) => token + 1);
      return;
    }
    setEntity((current) => {
      if (!current) return current;
      const docs = Array.isArray(current.documents) ? [...current.documents] : [];
      const index = docs.findIndex((doc) => String(doc.id) === String(updated.id));
      if (index >= 0) {
        docs[index] = { ...docs[index], ...updated };
      } else {
        docs.push(updated);
      }
      return { ...current, documents: docs };
    });
  };

  const documentsPanelProps = {
    documents: Array.isArray(entity?.documents) ? entity.documents : [],
    documentsUploadEnabled: canEditEntity && documentsUploadEnabled,
    // Raw instance AllowSkipKycLink (not gated by canEdit) — drives Veriff Gerar visibility.
    allowSkipKyc: documentsUploadEnabled,
    onDocumentsUploaded: handleDocumentsUploaded,
    onDocumentUpdated: handleDocumentUpdated,
    verificationEntity: entity,
  };

  if (!entityId) {
    return <Redirect to={ENTIDADES_LIST_PATH} />;
  }

  if (pageLoading || loading) {
    return <BaasEntidadesPageSkeleton />;
  }

  if (error || !entity) {
    return (
      <div className="baas-entidade-detail w-full min-w-0 space-y-4">
        <PageHeaderWithBack
          title={intl.get("baas.entidades.detail.pageTitle")}
          subTitle={intl.get("baas.entidades.detail.subtitle", { name: entityId })}
          onBack={() => {
            clearPeerOpsPin?.();
            history.push(ENTIDADES_LIST_PATH);
          }}
        />
        <div className="app-card p-6 text-sm text-red-500">{error || intl.get("baas.entidades.detail.notFound")}</div>
      </div>
    );
  }

  const displayProposalSituation = proposal?.situation || entity.proposalSituation || null;
  const hasValutAccount = accountsResolved
    ? linkedAccounts.length > 0
    : Boolean(entity.hasAccount);
  const displayStatus = resolveEntidadeDetailContaStatus({
    entity,
    linkedAccounts,
    accountsResolved,
    liveProposalSituation: displayProposalSituation,
  });

  const contaLabel = intl.get(`baas.entidades.status.${displayStatus}`);
  const kybLabel = intl.get(`baas.entidades.kybStatus.${entity.kybStatus}`);
  const isPersonEntity =
    entity.type === "pf" ||
    String(entity.id || "").startsWith("pers_");
  const accessStatus = entity.accessStatus || null;
  const awaitingFirstAccess = isBaasEntityAwaitingFirstAccess(entity);
  // Conta chip is the source of truth: Ativa → Desativar; suspended/blocked/deactivated → Ativar.
  const entityDeactivated =
    isEntidadeContaInactive(displayStatus)
    || (displayStatus !== "active"
      && displayStatus !== "pending"
      && isBaasEntityDeactivated(entity, linkedAccounts));
  const showApproveEntity =
    isValutAdmin
    && !valutAdminLoading
    && !entityDeactivated
    && entity.kybStatus
    && entity.kybStatus !== "approved";
  // Conta Digital / PaySmart MANUAL_APPROVED. Partners need allow_skip_kyc; Valut admin
  // may approve after Veriff/proposal reject even when Skip KYC is off. Keep visible for
  // pending and post-reject Conta chips (suspended/blocked → suspended).
  const contaAllowsManualApprove =
    displayStatus === "pending"
    || displayStatus === "suspended"
    || displayStatus === "blocked";
  const showApproveAccount =
    (documentsUploadEnabled || isValutAdmin)
    && canCreateAccount
    && hasValutAccount
    && !entityDeactivated
    && contaAllowsManualApprove
    && Boolean(resolveApproveAccountId(linkedAccounts.find((item) => resolveApproveAccountId(item))));
  const showResendAccess =
    isPersonEntity &&
    canResendAccess &&
    accessStatus &&
    accessStatus !== "no_email";
  const showCarteiraLogout =
    isValutMatrixConsolePin(selectedInstance)
    && !entity.isPeerInstanceOwner
    && isPersonEntity
    && entity.type !== "pj"
    && Boolean(entity.personId);
  const showTxPasswordReset =
    isValutAdmin
    && !valutAdminLoading
    && showCarteiraLogout;
  const carteiraOnline = Boolean(entity.loggedInCarteira);
  const carteiraPresence = resolveBaasPresenceDot({
    loggedIn: carteiraOnline,
    hasAccount: hasValutAccount,
    // Orange = truly no Conta Digital. With linked accounts, green/red is login only.
    accessStatus: hasValutAccount ? null : entity.accessStatus,
  });
  const carteiraPresenceLabel =
    carteiraPresence === "online"
      ? "baas.entidades.loggedInCarteira"
      : carteiraPresence === "none"
        ? !hasValutAccount && entity.accessStatus === "pending"
          ? "baas.users.access.status.pending"
          : "baas.entidades.noAccountCarteira"
        : "baas.entidades.loggedOutCarteira";
  const showDeactivateEntity =
    isValutAdmin
    && !valutAdminLoading
    && hasValutAccount
    && displayStatus !== "none"
    && !entityDeactivated;
  const showActivateEntity =
    isValutAdmin
    && !valutAdminLoading
    && hasValutAccount
    && entityDeactivated;

  const showGenerateLink = isPersonEntity && canCreateInvites;
  const showViewLinks = canCreateInvites;

  const showQuickActions =
    showApproveEntity
    || showResendAccess
    || showApproveAccount
    || showCarteiraLogout
    || showTxPasswordReset
    || showDeactivateEntity
    || showActivateEntity
    || showGenerateLink
    || showViewLinks;

  const resendEmail =
    entity?.raw?.person_details?.email ||
    entity?.raw?.personDetails?.email ||
    "";

  return (
    <div className="baas-entidade-detail relative w-full min-w-0 space-y-5">
      <PageHeaderWithBack
        title={intl.get("baas.entidades.detail.pageTitle")}
        subTitle={intl.get("baas.entidades.detail.subtitle", { name: entity.name })}
        onBack={() => {
          clearPeerOpsPin?.();
          history.push(ENTIDADES_LIST_PATH);
        }}
        className="mb-0"
      />

      {isInstanceOwner ? (
        <div className="flex flex-col gap-2 rounded-xl border border-[#e7a73f]/40 bg-[#fff8f0] px-4 py-3 text-sm text-[#7a5a28] dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10 dark:text-brand-gold-mid sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-2">
            <TbShield className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              <p className="font-semibold">
                {intl.get("baas.entidades.detail.ownerBanner")}
              </p>
              <p className="mt-0.5 text-xs opacity-90">
                {intl.get("baas.entidades.detail.ownerBannerHint")}
              </p>
            </div>
          </div>
          <Link
            to={BAAS_ROOT_PATH}
            className="btn-secondary inline-flex !h-8 w-auto shrink-0 items-center !text-xs self-start"
          >
            {intl.get("baas.entidades.detail.ownerBannerLink")}
          </Link>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-300">
            {intl.get(`baas.entidades.type.${entity.type}`)}
          </span>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[displayStatus] ?? statusStyles.none}`}
            title={intl.get("baas.entidades.columns.status")}
          >
            <span className="opacity-70">{intl.get("baas.entidades.chip.account")}:</span>
            {contaLabel}
          </span>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${kybStyles[entity.kybStatus] ?? ""}`}
            title={intl.get("baas.entidades.columns.kyb")}
          >
            <span className="opacity-70">{intl.get("baas.entidades.chip.entity")}:</span>
            {kybLabel}
          </span>
          {accessStatus ? (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${accessStatusStyles[accessStatus] ?? accessStatusStyles.pending}`}
              title={intl.get(`baas.users.access.hint.${accessStatus}`)}
            >
              <span className="opacity-70">{intl.get("baas.users.columns.access")}:</span>
              {intl.get(`baas.users.access.status.${accessStatus}`)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="min-w-0 break-all font-mono text-xs text-gray-400 dark:text-gray-500">
            {entity.id}
          </span>
          {showCarteiraLogout ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
              <BaasLoggedInDot
                loggedIn={carteiraOnline}
                hasAccount={hasValutAccount}
                accessStatus={hasValutAccount ? null : entity.accessStatus}
                titleKey="baas.entidades.loggedInCarteira"
                offlineTitleKey="baas.entidades.loggedOutCarteira"
                noAccountTitleKey="baas.entidades.noAccountCarteira"
              />
              {intl.get(carteiraPresenceLabel)}
            </span>
          ) : null}
        </div>
      </div>

      {displayProposalSituation && displayProposalSituation.tone !== "approved" ? (
        <div className="max-w-xl">
          <BaasProposalSituationCard
            situation={displayProposalSituation}
            entityKybApproved={entity.kybStatus === "approved"}
            hasValutAccount={hasValutAccount}
          />
        </div>
      ) : null}

      {approveError ? (
        <p role="alert" className="max-w-xl rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          {approveError}
        </p>
      ) : null}

      {isInstanceOwner && !isValutAdmin ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.detail.readOnlyBanner")}
        </p>
      ) : null}

      <BaasEntidadeRelatedAccountsPanel instance={apiInstance} entity={entity} />

      {showQuickActions ? (
        <section>
          <p className="dashboard-section-label mb-3">
            {intl.get("baas.entidades.detail.quickActionsSection")}
          </p>
          <div className="shortcut-scroll-row">
            {showGenerateLink ? (
              <ShortcutCard
                icon={TbLink}
                label={intl.get("baas.entidades.detail.verifications.generateButton")}
                onClick={() => setGenerateOpen(true)}
                title={intl.get("baas.entidades.detail.verifications.generateHint")}
              />
            ) : null}
            {showViewLinks ? (
              <ShortcutCard
                icon={TbList}
                label={intl.get("baas.entidades.detail.verifications.viewLinks")}
                onClick={() => history.push(BAAS_ONBOARD_PATH)}
                title={intl.get("baas.entidades.quickActions.onboardListHint")}
              />
            ) : null}
            {showApproveEntity ? (
              <ShortcutCard
                icon={TbCheck}
                label={
                  approvingEntity
                    ? intl.get("baas.entidades.detail.approveEntityLoading")
                    : intl.get("baas.entidades.detail.approveEntity")
                }
                onClick={() => {
                  void handleApproveEntity();
                }}
                disabled={approvingEntity}
                title={intl.get("baas.entidades.detail.approveEntityHint")}
              />
            ) : null}
            {showApproveAccount ? (
              <ShortcutCard
                icon={TbCheck}
                label={
                  approvingAccount
                    ? intl.get("baas.accounts.quickActions.approveAccountLoading")
                    : intl.get("baas.accounts.quickActions.approveAccount")
                }
                onClick={handleApproveAccount}
                disabled={approvingAccount}
                title={intl.get("baas.accounts.quickActions.approveAccountHint")}
              />
            ) : null}
            {showResendAccess ? (
              <ShortcutCard
                icon={TbMailForward}
                label={intl.get("baas.users.access.resend")}
                onClick={() => {
                  void handleResendAccessEmail();
                }}
                disabled={resendingAccess}
                title={intl.get("baas.users.access.resendHint")}
              />
            ) : null}
            {showCarteiraLogout ? (
              <ShortcutCard
                icon={TbLogout}
                label={intl.get("baas.entidades.logout")}
                onClick={() => {
                  void handleCarteiraLogout();
                }}
                disabled={!carteiraOnline}
                title={intl.get(carteiraPresenceLabel)}
              />
            ) : null}
            {showTxPasswordReset ? (
              <ShortcutCard
                icon={TbKey}
                label={intl.get("baas.entidades.txPasswordReset")}
                onClick={() => {
                  void handleResetTransactionalPassword();
                }}
                disabled={awaitingFirstAccess}
                title={
                  awaitingFirstAccess
                    ? intl.get("baas.entidades.firstAccessRequiredHint")
                    : intl.get("baas.entidades.txPasswordResetHint")
                }
              />
            ) : null}
            {showDeactivateEntity ? (
              <ShortcutCard
                icon={TbTrash}
                label={
                  deactivating
                    ? intl.get("baas.entidades.deactivateLoading")
                    : intl.get("baas.entidades.deactivate")
                }
                onClick={() => {
                  handleDeactivateEntity();
                }}
                disabled={deactivating}
                title={intl.get("baas.entidades.deactivateHint")}
              />
            ) : null}
            {showActivateEntity ? (
              <ShortcutCard
                icon={TbPlayerPlay}
                label={
                  activating
                    ? intl.get("baas.entidades.activateLoading")
                    : intl.get("baas.entidades.activate")
                }
                onClick={() => {
                  handleActivateEntity();
                }}
                disabled={activating}
                title={intl.get("baas.entidades.activateHint")}
              />
            ) : null}
          </div>
        </section>
      ) : null}

      <div
        className="extract-view-tabs extract-view-tabs--inline border-b border-gray-100 dark:border-brand-dark-border"
        role="tablist"
        aria-label={intl.get("baas.entidades.detail.tabsLabel")}
      >
        {DETAIL_TABS.map((tab) => {
          const isActive = detailTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`extract-view-tab link-no-zoom ${isActive ? "extract-view-tab--active" : ""}`}
              onClick={() => setDetailTab(tab.id)}
            >
              {intl.get(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {detailTab === "produtos" ? (
        <BaasEntidadeProductsPanel
          instance={apiInstance}
          entityId={entity.id || entityId}
        />
      ) : entity.type === "pf" ? (
        <BaasEntidadePersonalCreateForm
          key={entity.id}
          active
          editMode={canEditEntity}
          readOnly={!canEditEntity}
          unlockIdentity={isValutAdmin}
          entityId={entity.id}
          initialForm={personalForm}
          fixedInstanceId={apiInstance?.id ?? entity.instanceId}
          onUpdated={handleEntityUpdated}
          {...documentsPanelProps}
        />
      ) : (
        <div className="app-card p-6">
          <BaasEntidadeCreateForm
            key={entity.id}
            active
            editMode={canEditEntity}
            readOnly={!canEditEntity}
            unlockIdentity={isValutAdmin}
            entityId={entity.id}
            variant="full"
            initialForm={businessForm}
            fixedInstanceId={apiInstance?.id ?? entity.instanceId}
            onUpdated={handleEntityUpdated}
            {...documentsPanelProps}
          />
        </div>
      )}

      <BaasEntidadeKycGenerateModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        instance={apiInstance}
        entityId={entity.id || entityId}
        canCreateInvite={canCreateInvites}
        inviteFeatureEnabled={inviteFeatureEnabled}
        onViewAllLinks={() => {
          setGenerateOpen(false);
          history.push(BAAS_ONBOARD_PATH);
        }}
      />
      <BaasConfirmModal
        open={confirmKind === "resend"}
        onClose={closeConfirmModal}
        onConfirm={handleResendAccessConfirmed}
        title={intl.get("baas.users.access.resendConfirmTitle")}
        message={intl.get("baas.users.access.resendConfirm", { email: resendEmail })}
        confirmLabel={intl.get("baas.users.access.resend")}
        busyLabel={intl.get("baas.users.access.resend")}
        busy={resendingAccess}
        error={confirmKind === "resend" ? confirmError : ""}
      />
      <BaasConfirmModal
        open={confirmKind === "deactivate"}
        onClose={closeConfirmModal}
        onConfirm={handleDeactivateConfirmed}
        title={intl.get("baas.entidades.deactivateTitle")}
        message={intl.get("baas.entidades.deactivateConfirm")}
        confirmLabel={intl.get("baas.entidades.deactivate")}
        busyLabel={intl.get("baas.entidades.deactivateLoading")}
        busy={deactivating}
        error={confirmKind === "deactivate" ? confirmError : ""}
      >
        <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-brand-dark dark:text-gray-100">
          <input
            type="checkbox"
            checked={retainBalance}
            onChange={(event) => setRetainBalance(event.target.checked)}
            className="mt-0.5 accent-[#e7a73f]"
            disabled={deactivating}
          />
          <span>
            {intl.get("baas.entidades.deactivateRetainCheckbox")}
            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.entidades.deactivateRetainHint")}
            </span>
          </span>
        </label>
      </BaasConfirmModal>
      <BaasConfirmModal
        open={confirmKind === "activate"}
        onClose={closeConfirmModal}
        onConfirm={handleActivateConfirmed}
        title={intl.get("baas.entidades.activateTitle")}
        message={intl.get("baas.entidades.activateConfirm")}
        confirmLabel={intl.get("baas.entidades.activate")}
        busyLabel={intl.get("baas.entidades.activateLoading")}
        busy={activating}
        error={confirmKind === "activate" ? confirmError : ""}
      />
    </div>
  );
};
